import { useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { showToast } from "../primitives";
import { PanelRow } from "../cockpit/Panel";
import { useCreateRowFolder, useUploadRowFile } from "../../hooks/use-row-detail";
import { fetchFile } from "../../hooks/use-list-files";
import { formatRelativeTime } from "../../lib/v3-columns";
import type { FileMetadata, OwnedFolder } from "../../../lib/types";

/**
 * Sprint 12 — a row's owned files and folders. Reads the lists from
 * get_row_detail's `owned` payload; Upload and New-folder write against the
 * row's entity_id, which lazily promotes the row server-side on first content
 * (the user never sees "promote"). Folders open as cockpit nodes; files open
 * via a signed URL.
 */

interface RowFilesSectionProps {
  entityTypeSlug: string;
  entityId: string;
  files: FileMetadata[];
  folders: OwnedFolder[];
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileIcon(file: FileMetadata): string {
  const ext = /\.([a-z0-9]+)$/i.exec(file.filename)?.[1]?.toLowerCase() ?? "";
  const mime = file.mime_type ?? "";
  if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "ti-photo";
  if (ext === "pdf") return "ti-file-type-pdf";
  if (ext === "csv") return "ti-file-type-csv";
  if (ext === "md" || ext === "markdown") return "ti-markdown";
  return "ti-paperclip";
}

export function RowFilesSection({ entityTypeSlug, entityId, files, folders }: RowFilesSectionProps) {
  const navigate = useNavigate();
  const upload = useUploadRowFile();
  const createFolder = useCreateRowFolder();
  const inputRef = useRef<HTMLInputElement>(null);
  const [newFolder, setNewFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [opening, setOpening] = useState<string | null>(null);

  async function handleFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    for (const file of Array.from(list)) {
      try {
        await upload.mutateAsync({ file, entityTypeSlug, entityId });
        showToast(`Uploaded ${file.name}`);
      } catch (err) {
        showToast(`Couldn't upload ${file.name}: ${err instanceof Error ? err.message : "failed"}`, "error", true);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  async function submitFolder() {
    const name = folderName.trim();
    if (!name) {
      setNewFolder(false);
      return;
    }
    try {
      await createFolder.mutateAsync({ name, entityTypeSlug, entityId });
      showToast(`Created folder ${name}`);
    } catch (err) {
      showToast(`Couldn't create folder: ${err instanceof Error ? err.message : "failed"}`, "error", true);
    }
    setFolderName("");
    setNewFolder(false);
  }

  async function openFile(file: FileMetadata) {
    setOpening(file.id);
    try {
      const result = await fetchFile(file.id, "url");
      if (result.ok && result.delivery === "url") window.open(result.url, "_blank", "noopener");
    } catch {
      showToast(`Couldn't open ${file.filename}`, "error", true);
    } finally {
      setOpening(null);
    }
  }

  const empty = folders.length === 0 && files.length === 0;

  return (
    <div>
      <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} />

      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <button onClick={() => setNewFolder(true)} style={actionButtonStyle}>
          <i className="ti ti-folder-plus" style={{ fontSize: 13 }} aria-hidden="true" />
          New folder
        </button>
        <button onClick={() => inputRef.current?.click()} disabled={upload.isPending} style={actionButtonStyle}>
          <i className={`ti ${upload.isPending ? "ti-loader-2" : "ti-upload"}`} style={{ fontSize: 13 }} aria-hidden="true" />
          {upload.isPending ? "Uploading…" : "Upload"}
        </button>
      </div>

      {newFolder && (
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <input
            autoFocus
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitFolder();
              if (e.key === "Escape") { setNewFolder(false); setFolderName(""); }
            }}
            placeholder="Folder name"
            style={{
              flex: 1,
              padding: "5px 8px",
              fontSize: 13,
              fontFamily: "var(--font-sans)",
              border: "0.5px solid var(--c-pink-600)",
              borderRadius: "var(--border-radius-sm)",
              outline: "none",
            }}
          />
          <button onClick={submitFolder} disabled={createFolder.isPending} style={actionButtonStyle}>
            Add
          </button>
        </div>
      )}

      {empty && !newFolder ? (
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "6px 0" }}>
          No files or folders yet.
        </div>
      ) : (
        <>
          {folders.map((folder) => (
            <PanelRow
              key={folder.id}
              onClick={() => navigate(`/agents/${folder.id}`)}
              title={folder.name}
              trailing={folder.child_count > 0 ? <span style={metaStyle}>{folder.child_count} item{folder.child_count === 1 ? "" : "s"}</span> : undefined}
            >
              <i className="ti ti-folder" style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
              <span style={nameStyle}>{folder.name}</span>
            </PanelRow>
          ))}
          {files.map((file) => (
            <PanelRow
              key={file.id}
              onClick={() => openFile(file)}
              title={file.filename}
              trailing={<span style={metaStyle}>{opening === file.id ? "opening…" : `${formatSize(file.size)} · ${formatRelativeTime(file.created_at)}`}</span>}
            >
              <i className={`ti ${fileIcon(file)}`} style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
              <span style={nameStyle}>{file.filename}</span>
            </PanelRow>
          ))}
        </>
      )}
    </div>
  );
}

const actionButtonStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const nameStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const metaStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};
